import React, { useState, useEffect } from "react";
import { Modal, Checkbox, Button } from "@multiagentbook/ui";
import { CheckCircle } from "lucide-react";

interface Dimension {
  slug: string;
  name: string;
  questions?: { question: string }[];
}

interface DimensionSelectorModalProps {
  isVisible: boolean;
  allDimensions: Dimension[];
  selectedSlugs: string[];
  onOk: (selectedSlugs: string[]) => void;
  onCancel: () => void;
  allDimensionsFull?: Array<{
    slug: string;
    name: string;
    questions: { question: string }[];
  }>;
}

const DimensionSelectorModal: React.FC<DimensionSelectorModalProps> = ({
  isVisible,
  allDimensions,
  selectedSlugs,
  onOk,
  onCancel,
  allDimensionsFull,
}) => {
  const [checkedList, setCheckedList] = useState<string[]>(selectedSlugs);
  const [checkAll, setCheckAll] = useState(
    selectedSlugs.length === allDimensions.length
  );
  const [indeterminate, setIndeterminate] = useState(
    selectedSlugs.length > 0 && selectedSlugs.length < allDimensions.length
  );

  useEffect(() => {
    setCheckedList(selectedSlugs);
    const allSelected = selectedSlugs.length === allDimensions.length;
    setCheckAll(allSelected);
    setIndeterminate(selectedSlugs.length > 0 && !allSelected);
  }, [selectedSlugs, allDimensions]);

  const onChange = (slug: string) => {
    let newList: string[];
    if (checkedList.includes(slug)) {
      newList = checkedList.filter((s) => s !== slug);
    } else {
      newList = [...checkedList, slug];
    }
    setCheckedList(newList);
    setIndeterminate(!!newList.length && newList.length < allDimensions.length);
    setCheckAll(newList.length === allDimensions.length);
  };

  const onCheckAllChange = (checked: boolean) => {
    const newCheckedList = checked
      ? allDimensions.map((d) => d.slug)
      : [];
    setCheckedList(newCheckedList);
    setIndeterminate(false);
    setCheckAll(checked);
  };

  const handleOk = () => {
    onOk(checkedList);
  };

  return (
    <Modal
      title="Select Dimensions"
      open={isVisible}
      onCancel={onCancel}
      width="large"
      footer={
        <div className="flex justify-end gap-2">
          <Button onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleOk}>
            Apply
          </Button>
        </div>
      }
    >
      <Checkbox
        indeterminate={indeterminate}
        onChange={onCheckAllChange}
        checked={checkAll}
        style={{
          marginBottom: "10px",
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: "10px",
        }}
      >
        <span className="text-primary">Select All</span>
      </Checkbox>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
        {allDimensions.map((dim) => {
          const selected = checkedList.includes(dim.slug);
          // Always get questions from allDimensionsFull if not present
          let questions = dim.questions;
          if ((!questions || questions.length === 0) && allDimensionsFull) {
            const found = allDimensionsFull.find((d) => d.slug === dim.slug);
            if (found && found.questions) questions = found.questions;
          }
          return (
            <div
              key={dim.slug}
              className={`rounded-lg border transition-all cursor-pointer p-4 relative bg-primary
                ${
                  selected
                    ? "border-accent ring-2 ring-accent/30"
                    : "border-secondary/10"
                }
                hover:bg-secondary hover:border-accent/60`}
              onClick={() => onChange(dim.slug)}
            >
              <div className="flex items-center mb-2">
                <span className="font-semibold text-primary text-base flex-1">
                  {dim.name}
                </span>
                {selected && (
                  <CheckCircle className="text-accent w-5 h-5 ml-2" />
                )}
              </div>
              {questions && questions.length > 0 && (
                <ul className="list-disc pl-5 text-xs text-secondary space-y-1">
                  {questions.slice(0, 3).map((q, idx) => (
                    <li key={idx}>{q.question}</li>
                  ))}
                  {questions.length > 3 && (
                    <li className="italic text-secondary/60">...and more</li>
                  )}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default DimensionSelectorModal;
